import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { useRazorpayCheckout } from "@/hooks/useRazorpayCheckout";
import StudentCard from "@/components/parent/StudentCard";
import StudentDialog from "@/components/parent/StudentDialog";
import VerifyEmailBanner from "@/components/parent/VerifyEmailBanner";
import { FullPageLoader } from "@/components/ProtectedRoute";

function EmptyState({ onAdd }) {
  return (
    <div className="rounded-[24px] bg-white border border-ds-plum/10 p-10 sm:p-14 text-center" data-testid="students-empty-state">
      <p className="font-serif text-3xl italic text-ds-plum">Let's begin.</p>
      <p className="mt-2 text-ds-inksoft max-w-md mx-auto">Add your child's profile with their current class. You can add more than one child.</p>
      <button onClick={onAdd} className="ds-btn mt-6 inline-flex items-center gap-2 bg-ds-plum text-white hover:bg-[#3d0232]" data-testid="empty-add-student-button"><Plus className="h-4 w-4" />Add a student</button>
    </div>
  );
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const { data: students, loading, reload } = useFetch("/students");
  const { data: cfg } = useFetch("/public/config");
  const [dialog, setDialog] = useState({ open: false, student: null });
  const pay = useRazorpayCheckout(reload);
  const navigate = useNavigate();
  const start = async (student) => {
    try {
      const { data } = await api.post(`/students/${student.id}/session/start`);
      navigate(`/dashboard/assessment/${data.session_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    if (!students?.some((s) => s.status === "report_generating")) return undefined;
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [students, reload]);

  useEffect(() => {
    if (params.get("welcome")) {
      toast.success("Account created. Please check your inbox to verify your email.");
      setParams({}, { replace: true });
    }
    refreshUser().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !students) return <FullPageLoader />;
  const openAdd = () => setDialog({ open: true, student: null });

  return (
    <div className="space-y-8" data-testid="parent-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-ds-plum">Parent dashboard</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="dashboard-greeting">Hello, {user?.name?.split(" ")[0]}</h1>
        </div>
        {students?.length > 0 && <button onClick={openAdd} className="ds-btn bg-ds-plum text-white hover:bg-[#3d0232]" data-testid="add-student-button"><Plus className="h-4 w-4" />Add student</button>}
      </div>
      {!user?.email_verified && <VerifyEmailBanner email={user?.email} />}
      {cfg && !cfg.payments_enabled && students?.some((s) => s.status === "payment_required") && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 flex gap-3 text-sm text-blue-800" data-testid="payments-setup-notice">
          <Info className="h-5 w-5 shrink-0" /> Online payment is being set up. To pay offline, write to support@campussaarthi.com and our team will unlock the assessment.
        </div>
      )}
      {students?.length === 0 ? <EmptyState onAdd={openAdd} /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {students?.map((s) => <StudentCard key={s.id} student={s} price={cfg?.price_inr ?? 499} onPay={pay} onStart={start} onEdit={(st) => setDialog({ open: true, student: st })} />)}
        </div>
      )}
      <StudentDialog open={dialog.open} student={dialog.student} onOpenChange={(open) => setDialog({ ...dialog, open })} onSaved={reload} />
    </div>
  );
}
